import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Feature } from "geojson";

import { api } from "../utils/api";
import { ApiError, ApiResponse } from "../types/api";

/**
 * Project property boundaries — added from a filestore file after creation
 * (the wizard's property-file step covers creation). The layer reads
 * `project.data.properties`, so mutations invalidate the project query.
 */

export interface ImportPropertiesPayload {
  filestoreFileId: number;
  /** Feature property holding the property name; absent = "Property N". */
  nameField?: string | null;
  /** Delete the project's existing properties first. */
  replace?: boolean;
}

export interface ImportPropertiesResult {
  created: number;
  total: number;
}

export const useImportProperties = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<ImportPropertiesResult>, ApiError, ImportPropertiesPayload>({
    mutationFn: (payload) =>
      api.request<ApiResponse<ImportPropertiesResult>>(`/api/projects/${projectId}/properties`, {
        method: "POST",
        // api.request JSON-encodes the body itself; pre-stringifying here
        // double-encodes it and the server sees a string, not an object.
        body: {
          filestore_file_id: payload.filestoreFileId,
          name_field: payload.nameField || null,
          replace: !!payload.replace,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

export const useDeleteAllProperties = (projectId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<{ deleted: number }>, ApiError, void>({
    mutationFn: () =>
      api.request<ApiResponse<{ deleted: number }>>(`/api/projects/${projectId}/properties`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });
};

/** GET /api/projects/properties?ids=…: every named project's property
 *  boundaries in one collection. Each feature's `project_id` says whose it is. */
export interface ProjectsPropertiesCollection {
  type: "FeatureCollection";
  features: Feature[];
  /** The projects that were found (the caller's own). */
  project_ids: number[];
  /** How many of them contributed at least one property. */
  project_count: number;
  /** Projects whose parcels were cut short by the server's caps. */
  truncated_project_ids: number[];
}

/** Projects per request; the server allows 500, this keeps URLs short. */
const PROPERTIES_CHUNK = 200;

/**
 * The property boundaries of `projectIds` for the listing map, fetched in one
 * go (chunked) and cached for the set of ids. Off while `enabled` is false so
 * nothing is pulled while the layer is hidden.
 */
export const useProjectsProperties = (projectIds: Array<number | string>, enabled = true) => {
  const key = useMemo(
    () => Array.from(new Set(projectIds.map(String))).sort().join(","),
    [projectIds]
  );
  const { data, isLoading, error } = useQuery<ProjectsPropertiesCollection, ApiError>({
    queryKey: ["projects-properties", key],
    enabled: enabled && key.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const ids = key.split(",");
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += PROPERTIES_CHUNK) chunks.push(ids.slice(i, i + PROPERTIES_CHUNK));
      const parts = await Promise.all(
        chunks.map((chunk) =>
          api.request<ApiResponse<ProjectsPropertiesCollection>>(`/api/projects/properties?ids=${chunk.join(",")}`)
        )
      );
      const merged: ProjectsPropertiesCollection = {
        type: "FeatureCollection",
        features: [],
        project_ids: [],
        project_count: 0,
        truncated_project_ids: [],
      };
      parts.forEach((res) => {
        const part = res?.data;
        if (!part) return;
        merged.features.push(...part.features);
        merged.project_ids.push(...part.project_ids);
        merged.project_count += part.project_count;
        merged.truncated_project_ids.push(...part.truncated_project_ids);
      });
      return merged;
    },
  });
  const active = enabled && key.length > 0;
  return { properties: active ? data ?? null : null, isLoading: active && isLoading, error };
};
